import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { flowNodeColor, FlowType } from 'fable/flowEditor';
import { hexStringToNumber } from 'fable/flowEditor/components/flowManager/canvas/renderers/common/colors';
import type {
  EmailExtraData,
  PhoneExtraData,
} from 'fable/flowEditor/components/flowManager/canvas/types/extraData';
import type {
  GenericNodeAttributes,
  GenericNodeProps,
  NodeConnectionTag,
} from 'fable/flowEditor/components/flowManager/canvas/types/node';
import { Text } from 'flow-builder/node/text';
import { TextContent } from 'flow-builder/node/textContent';
import { TextEllipsis } from 'flow-builder/node/textEllipsis';

import { createBusinessHoursModal } from '../../modals/businesshours/businesshours';

export type BusinessHoursPayload = {
  scheduleId: string;
  hangUpAfterAnnouncement: boolean;
  closedNodeId: string;
  openNodeId: string;
};

type BusinessHoursNodeAttributes = GenericNodeAttributes<
  BusinessHoursPayload,
  PhoneExtraData | EmailExtraData
>;
type BusinessHoursNodeProps = GenericNodeProps<
  BusinessHoursPayload,
  PhoneExtraData | EmailExtraData
>;

const callClosedEdgeTag: NodeConnectionTag = {
  icon: 'announcement',
  text: 'Play audio message',
};

const getClosedEdgeTag = (
  flowType: FlowType,
  isDefaultAutoReplySet = false,
): NodeConnectionTag | undefined => {
  switch (flowType) {
    case FlowType.FLOW_TYPE_MESSENGER: {
      return undefined;
    }
    case FlowType.FLOW_TYPE_CALL: {
      return callClosedEdgeTag;
    }
    default: {
      return isDefaultAutoReplySet
        ? {
            icon: 'announcement',
            text: 'Send auto-reply',
          }
        : undefined;
    }
  }
};

export const createTranslatePayload =
  (flowType: FlowType): BusinessHoursNodeAttributes['translatePayload'] =>
  (node, extraData) => {
    const { schedules } = extraData;
    const { payload, connections } = node;
    const { closedNodeId, openNodeId, hangUpAfterAnnouncement } = payload;
    const isPhone = flowType === FlowType.FLOW_TYPE_CALL;
    const existingVisualConnection = connections.find(
      ({ connection }) => connection == null,
    );
    const schedule = schedules.find(({ id }) => id === payload?.scheduleId);

    return [
      isPhone && hangUpAfterAnnouncement
        ? {
            id: existingVisualConnection?.id || uuid(),
            label: 'Closed',
            tag: callClosedEdgeTag,
          }
        : {
            id: uuid(),
            connection: closedNodeId || uuid(),
            label: 'Closed',
            tag: getClosedEdgeTag(
              flowType,
              schedule?.is_default_auto_reply_set,
            ),
          },
      {
        id: uuid(),
        connection: openNodeId || uuid(),
        label: 'Open',
      },
    ];
  };

const updateConnection: BusinessHoursNodeAttributes['updateConnection'] = (
  node,
  connection,
  connectionId,
) => {
  const entryName =
    connection.connectionLabel === 'Open' ? 'openNodeId' : 'closedNodeId';
  const payload: BusinessHoursPayload = {
    ...node.payload,
    [entryName]: connectionId,
  };

  return { ...node, payload };
};

const createBusinessHoursNodeAttributes = (
  flowType: FlowType,
): BusinessHoursNodeAttributes => ({
  iconName: 'clock',
  label: 'Business hours',
  color: hexStringToNumber(flowNodeColor.purple),
  helpText:
    'By setting your opening hours, you can decide what happens to incoming conversations based on whether your business is open or closed.',
  translatePayload: createTranslatePayload(flowType),
  updateConnection,
  NodeContent: BusinessHoursNode,
  ActionModal: createBusinessHoursModal(flowType),
});

export const BusinessHoursPhoneNodeAttributes =
  createBusinessHoursNodeAttributes(FlowType.FLOW_TYPE_CALL);

export const BusinessHoursEmailNodeAttributes =
  createBusinessHoursNodeAttributes(FlowType.FLOW_TYPE_EMAIL);

/** Business hours node component pre-configured for the messenger flow */
export const BusinessHoursMessengerNodeAttributes =
  createBusinessHoursNodeAttributes(FlowType.FLOW_TYPE_MESSENGER);

function BusinessHoursNode({
  node,
  context: { extraData },
}: BusinessHoursNodeProps): JSX.Element {
  const { schedules } = extraData;
  const { scheduleId } = node.payload;

  return (
    <TextContent>
      {useMemo(() => {
        const schedule = schedules.find(({ id }) => id === scheduleId);
        const name = schedule?.name || 'unknown schedule';

        return (
          <TextEllipsis>
            <Text weight="normal" text="Schedule" />
            <Text weight="semibold" text={name} />
          </TextEllipsis>
        );
      }, [scheduleId, schedules])}
    </TextContent>
  );
}
